import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getInternalIntegrationTestingIssueOptionsQueryKey,
  useInternalIntegrationTestingIssueOptions,
} from "gen/endpoints/integration-testing-issue-options/integration-testing-issue-options";
import {
  getInternalIntegrationTestingIssuesTableQueryKey,
  useIntegrationTestingIssueCreate,
} from "gen/endpoints/integration-testing-issues/integration-testing-issues";
import { IntegrationTestTypeEnum } from "gen/model/integrationTestTypeEnum";
import { IntegrationTestVersionTypeEnum } from "gen/model/integrationTestVersionTypeEnum";
import { InternalIntegrationTestingIssueOptionsTestType } from "gen/model/internalIntegrationTestingIssueOptionsTestType";
import { TestingReportIssueCreateDataRequest } from "gen/model/testingReportIssueCreateDataRequest";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormTextInput } from "shared/components/input/text/TextInput";

export interface InternalIntegrationTestingTicketModalProps {
  onCloseComplete(): void;
}

export const InternalIntegrationTestingTicketModal: React.FC<
  InternalIntegrationTestingTicketModalProps
> = ({ onCloseComplete }) => {
  const { t } = useTranslation(["internal", "auth", "common"]);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const form = useForm<TestingReportIssueCreateDataRequest>();

  const selectedVersionType = useWatch({
    control: form.control,
    name: "version_type",
  }) as IntegrationTestVersionTypeEnum | null;

  const selectedTestType = useWatch({
    control: form.control,
    name: "test_type",
  }) as InternalIntegrationTestingIssueOptionsTestType | null;

  const { data: issueSelectOptions } =
    useInternalIntegrationTestingIssueOptions(
      {
        test_type: selectedTestType!,
        version_type: selectedVersionType!,
      },
      {
        query: {
          enabled: !!selectedTestType && !!selectedVersionType,
        },
      },
    );
  const { mutateAsync: createIssue } = useIntegrationTestingIssueCreate();

  const queryClient = useQueryClient();

  const onSubmit = async (data: TestingReportIssueCreateDataRequest) => {
    await createIssue({ data });
    await Promise.allSettled([
      queryClient.invalidateQueries({
        queryKey: getInternalIntegrationTestingIssuesTableQueryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: getInternalIntegrationTestingIssueOptionsQueryKey(),
      }),
    ]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={onClose}
          successMessage={t("internal:modal.createTicket.success")}
        >
          <ModalHeader>
            {t("internal:modal.createTicket.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <FormSelect
              title={t("internal:modal.createTicket.fields.versionType")}
              name="version_type"
            >
              {Object.values(IntegrationTestVersionTypeEnum).map(
                (versionType) => (
                  <SelectOption
                    key={`issue_test_name_${versionType}`}
                    value={versionType}
                  >
                    {t(`enums:IntegrationVersionTypeEnum.${versionType}`)}
                  </SelectOption>
                ),
              )}
            </FormSelect>
            <FormSelect
              title={t("internal:modal.createTicket.fields.testType")}
              name="test_type"
            >
              {Object.values(IntegrationTestTypeEnum).map((testType) => (
                <SelectOption
                  key={`issue_test_name_${testType}`}
                  value={testType}
                >
                  {t(`enums:IntegrationTestTypeEnum.${testType}`)}
                </SelectOption>
              ))}
            </FormSelect>
            {!!selectedTestType && (
              <FormSelect
                title={t("internal:modal.createTicket.fields.testName")}
                name="test_name"
              >
                {issueSelectOptions?.map(({ value, rich_value }) => (
                  <SelectOption key={`issue_test_name_${value}`} value={value}>
                    {rich_value}
                  </SelectOption>
                ))}
              </FormSelect>
            )}
            <FormTextInput
              maxLength={4}
              title={t("internal:modal.createTicket.fields.ticketNumber")}
              name="ticket_number"
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={<FormSubmitButton>{t("common:save")}</FormSubmitButton>}
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
